import { Form, Select, TreeSelect } from "antd"
import { ChevronDownIcon } from "lucide-react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { useGetOrbitshiftLocations } from "~/hooks/accounts"
import { getLocationDisplay, getLocationKey } from "~/utils/location-helpers"

export const SENIORITY_OPTIONS: Record<string, string> = {
  c_suite: "Founder, C_suite",
  vp: "Vp",
  director: "Director",
  head: "Head"
}

const SENIORITY_ITEMS = [
  { key: "c_suite", i18nKey: "pre_meeting_brief.seniority_c_suite" },
  { key: "vp", i18nKey: "pre_meeting_brief.seniority_vp" },
  { key: "director", i18nKey: "pre_meeting_brief.seniority_director" },
  { key: "head", i18nKey: "pre_meeting_brief.seniority_head" }
] as const

const SHOW_PARENT = TreeSelect.SHOW_CHILD

export const SearchSeniorities = ({ disabled }: { disabled: boolean }) => {
  const { t } = useTranslation()

  const seniorityOptions = useMemo(
    () =>
      SENIORITY_ITEMS.map(({ key, i18nKey }) => ({
        label: t(i18nKey),
        value: key
      })),
    [t]
  )

  return (
    <>
      <Form.Item name="person_seniorities" className="mb-0 flex-1">
        <Select
          mode="multiple"
          className={`custom-select-filter h-9 input-field rounded-[10px] w-1/2 ${
            disabled
              ? "!bg-gray-300 dark:!bg-dark-2"
              : "!bg-white dark:!bg-dark-1"
          }`}
          placeholder={t("pre_meeting_brief.placeholder_any_seniority_level")}
          showSearch
          allowClear
          disabled={disabled}
          options={seniorityOptions}
          suffixIcon={
            <ChevronDownIcon className="w-4 h-4 dark:text-dark-6 text-dark-2" />
          }
        />
      </Form.Item>
    </>
  )
}

export const SearchLocations = ({ disabled }: { disabled: boolean }) => {
  const { t } = useTranslation()
  const { data: defaultLocations } = useGetOrbitshiftLocations()

  const treeData = useMemo(
    () =>
      defaultLocations?.data?.locations?.map((item: any) => {
        const regionKey = getLocationKey(item.region_name) || item.region_code
        return {
          key: regionKey,
          title: getLocationDisplay(item.region_name) || item.region_name,
          value: regionKey,
          children: (item.countries ?? []).map((_item: any) => {
            const countryKey = getLocationKey(_item.countryName)
            return {
              key: countryKey,
              title: getLocationDisplay(_item.countryName) || _item.countryName,
              value: countryKey
            }
          })
        }
      }),
    [defaultLocations]
  )

  const tProps = useMemo(
    () => ({
      treeData,
      treeCheckable: true,
      showCheckedStrategy: SHOW_PARENT,
      placeholder: t("pre_meeting_brief.placeholder_any_location"),
      maxTagCount: 2,
      style: {
        width: "100%",
        flex: 1
      },
      treeNodeFilterProp: "title",
      treeNodeLabelProp: "title"
    }),
    [treeData]
  )

  return (
    <>
      <Form.Item name="person_locations" className="flex-1 w-full mb-0">
        <TreeSelect
          className={`custom-select-filter h-9 input-field rounded-[10px] w-1/2 ${
            disabled
              ? "!bg-gray-300 dark:!bg-dark-2"
              : "!bg-white dark:!bg-dark-1"
          }`}
          {...tProps}
          allowClear
          treeCheckable
          disabled={disabled}
          treeDefaultExpandAll
          virtual
          suffixIcon={
            <ChevronDownIcon className="w-4 h-4 dark:text-dark-6 text-dark-2" />
          }
        />
      </Form.Item>
    </>
  )
}
